import React, { useCallback, useContext, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import moment from 'moment'

import { getIsPrivateRequestEnabled, getIsTourOfferPurchasable, getSelectedVariationDepartureBoundaries } from 'selectors/TourV2/tourV2OfferPageSelectors'
import { getShareModalConfig, ShareModalConfig } from 'lib/getShareModalConfig'
import { LONG_MONTH_LONG_YEAR } from 'constants/dateFormats'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { TourV2ContentSections } from 'constants/tours'
import BodyText from 'components/Luxkit/Typography/BodyText'
import FormatCurrency from 'components/Common/FormatCurrency'
import Heading from 'components/Luxkit/Typography/Heading'
import Hr from 'components/Common/Hr'
import Image from 'components/Common/Image'
import TextLink from 'components/Luxkit/TextLink'
import MarkdownRender from 'components/Luxkit/MarkdownRender'
import LineClockIcon from 'components/Luxkit/Icons/line/LineClockIcon'
import LineGlobeIcon from 'components/Luxkit/Icons/line/LineGlobeIcon'
import LineMapIcon from 'components/Luxkit/Icons/line/LineMapIcon'
import Pane from 'components/Common/Pane'
import ShareModal from 'components/Common/Modals/ShareModal'
import ShareWithFriendsButton from 'components/Common/ShareWithFriendsButton'
import TextButton from 'components/Luxkit/Button/TextButton'
import TourBookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton/TourBookmarkButton'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import useTourV2PurchasableOptionDetails from 'hooks/TourV2/useTourV2PurchasableOptionDetails'
import useTourV2VariationDetails from 'hooks/TourV2/useTourV2VariationDetails'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import CurrencyContext from 'contexts/currencyContext'
import DepositsAvailableTag from 'components/Common/SecureWithDeposit/DepositsAvailableTag'
import SecureWithDepositTagAndTooltip from 'components/Common/SecureWithDeposit/SecureWithDepositTagAndTooltip'
import { isTourV2DepositDateAllowed, isTourV2DepositEnabledForCurrency } from 'lib/TourV2/isTourV2DepositAllowed'
import UrgencyLabel from 'components/Luxkit/Label/UrgencyLabel'
import Group from 'components/utils/Group'
import ImmersiveSaveButtonPortal from 'tripPlanner/components/ImmersiveHeader/ImmersiveSaveButtonPortal'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Caption from 'components/Luxkit/Typography/Caption'
import Pill from 'components/Luxkit/Pill'
import UltraLuxUSPs from '../UltraLux/UltraLuxUSPs'
import PrimaryButtonPaletteProvider, { PrimaryButtonPalette } from 'contexts/PrimaryButtonPaletteProvider'
import { useTourV2PricePerDay } from 'hooks/TourV2/useTourV2PricePerDay'
import LineTagAltIcon from 'components/Luxkit/Icons/line/LineTagAltIcon'
import LineUserIcon from 'components/Luxkit/Icons/line/LineUserIcon'
import ModalContext from 'contexts/ModalContext'
import PrivateRequestFormModal from 'components/OfferPage/TourV2/PrivateRequest/PrivateRequestFormModal'
import TourV2USP from 'components/OfferPage/TourV2/TourV2USP'
import isUltraLuxTour from 'lib/TourV2/isUltraLuxTour'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import LineUsersAltIcon from 'components/Luxkit/Icons/line/LineUsersAltIcon'
import LineChartLineIcon from 'components/Luxkit/Icons/line/LineChartLineIcon'
import LineUserSquareIcon from 'components/Luxkit/Icons/line/LineUserSquareIcon'
import TourV2UrgencyTags from 'components/Tours/TourV2UrgencyTags'
import NumberRating from 'components/Common/NumberRating/NumberRating'
import TourV2ItineraryLocationList from 'components/OfferPage/TourV2/TourV2ItineraryLocationList'
import { unique } from 'lib/arrayUtils'
import { OptimizelyClientSideExperiments, useOptimizelyExperiment } from 'lib/optimizely'

const GridContainer = styled.div`
  display: grid;
  grid-template-areas:
    "top-bar"
    "."
    "title"
    "."
    "details"
    "."
    "review"
    "."
    "overview"
    "."
    "description";

  grid-template-columns: 1fr;
  grid-template-rows:
    min-content
    ${rem(8)}
    min-content
    ${rem(8)}
    min-content
    ${rem(16)}
    min-content
    ${rem(16)}
    min-content
    ${rem(16)}
    min-content;
  max-width: 100%;

  ${mediaQueryUp.tablet} {
    grid-template-areas:
      "top-bar top-bar top-bar"
      ". . ."
      "title . overview"
      "title . overview"
      "title . overview"
      ". . overview"
      "details . overview"
      ". . overview"
      "review . overview"
      ". . overview"
      "description . overview"
      ". . overview"; /* to create a fake empty area to push content up */
    grid-template-columns: 1fr ${rem(32)} ${rem(400)};
    grid-template-rows:
      min-content
      ${rem(8)}
      min-content
      ${rem(4)}
      min-content
      ${rem(12)}
      min-content
      ${rem(16)}
      min-content
      ${rem(16)}
      min-content;
  }

  > .top-area {
    grid-area: top;
  }

  > .tags-area {
    grid-area: tags;
  }

  > .top-bar-area {
    grid-area: top-bar;
  }

  > .title-area {
    grid-area: title;
  }

  > .details-area {
    grid-area: details;
  }

  > .description-area {
    grid-area: description;
  }

  > .review-area {
    grid-area: review;
  }

  > .overview-area {
    grid-area: overview;
  }
`

const OperatorLogoImage = styled(Image)`
  max-width: 100%;
  max-height: 100%;
`

const OperatorLogoContainer = styled.div`
  display: none;

  ${mediaQueryUp.desktop} {
    display: block;
    flex-shrink: 0;
    max-height: ${rem(75)};
    max-width: ${rem(150)};
    align-self: center;
  }
`

const Description = styled(MarkdownRender)`
  line-height: ${rem(24)};
`

const DetailItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(4)};

  ${mediaQueryUp.desktop} {
    flex-direction: row;
    gap: ${rem(16)};
  }

  &:empty {
    display: none;
  }
`

const VerticalDivider = styled.div`
  display: none;

  ${mediaQueryUp.desktop} {
    display: block;
    height: ${rem(24)};
    width: ${rem(1)};
    background-color: ${({ theme }) => theme.palette.neutral.six};
  }
`

const OverviewPane = styled(Pane)`
  border-radius: ${props => props.theme.borderRadius.small};
  border: 1px solid ${({ theme }) => theme.palette.neutral.six};
  box-shadow: ${props => props.theme.shadow.flat.small};
  display: flex;
  flex-direction: column;
  gap: ${rem(16)};
  padding: ${rem(20)};
`

const OverviewPaneDetailItemsContainer = styled.div`
  display: grid;
  gap: ${rem(8)};
  grid-auto-flow: row;
  grid-template-columns: repeat(2, 1fr);
`

const ValuedAtCaption = styled(Caption)`
  display: flex;
  align-items: center;
  gap: ${rem(4)};
`

const LabelsGroup = styled(Group)`
  &:empty {
    display: none;
  }
`

const ContainerExtend = styled.div`
  grid-column: span 2;
`

interface MappedProps {
  latestDeparture: Tours.V2OfferDeparture
  variation: Tours.V2OfferVariation
  offer: Tours.V2Offer
  isOfferPurchasable: boolean
  toursPricePerDayABTestVariation: string
  isPrivateRequestEnabled: boolean
}

interface Props {
  cheapestPurchasableOption: Tours.V2OfferPurchasableOption
  onCheckAvailability: () => void
  onRequestPrivateTour: () => void
  onView: (ref: string) => void
  shareModalConfig: ShareModalConfig
  buttonPaletteValue: PrimaryButtonPalette
}

function TourV2Overview(props: Props & MappedProps) {
  const {
    cheapestPurchasableOption,
    isOfferPurchasable,
    isPrivateRequestEnabled,
    latestDeparture,
    offer,
    onCheckAvailability,
    onRequestPrivateTour,
    onView,
    shareModalConfig,
    toursPricePerDayABTestVariation,
    variation,
    buttonPaletteValue,
  } = props
  const currentCurrency = useContext(CurrencyContext)

  // operator
  const operatorName = offer.brand?.name
  const operatorLogo = offer.brand?.logoImage

  // Activity level
  const activityLevel = offer.activityLevel

  // last available booking date
  const { lastAvailableBookingDate, showSecureWithDepositTag } = useMemo(() => {
    if (latestDeparture.status !== 'available') {
      return {
        lastAvailableBookingDate: undefined,
        showSecureWithDepositTag: false,
      }
    }
    const lastDate = moment(latestDeparture.startDate).format(LONG_MONTH_LONG_YEAR)
    return {
      lastAvailableBookingDate: lastDate,
      showSecureWithDepositTag: offer.depositType !== 'noDeposit' && isTourV2DepositEnabledForCurrency(currentCurrency) && isTourV2DepositDateAllowed(lastDate),
    }
  }, [latestDeparture, currentCurrency, offer.depositType])

  const purchasableOptionDetails = useTourV2PurchasableOptionDetails(cheapestPurchasableOption)
  const isTourReviewsABTestEnabled = !!useOptimizelyExperiment(OptimizelyClientSideExperiments.isTourReviewsEnabled)
  const isTourReviewsVisible = isTourReviewsABTestEnabled && offer.reviewsRating > 0 && offer.reviewsTotal > 0

  const {
    description,
    startLocation,
    endLocation,
    hasTheSameStartAndEndLocation,
    placesCount,
    durationInDaysCount,
    countriesCount,
    maxPax,
    minAge,
    maxAge,
  } = useTourV2VariationDetails(variation)

  const { isVariationLeading, pricePerDay } = useTourV2PricePerDay(offer, cheapestPurchasableOption, variation, toursPricePerDayABTestVariation)

  const shareConfig = getShareModalConfig(shareModalConfig)

  const scrollToDepartures = useCallback(
    () => {
      onCheckAvailability() // Track click on "Check availability" button
      onView(TourV2ContentSections.DEPARTURES)
    },
    [onView, onCheckAvailability],
  )

  const scrollToItinerary = useCallback(() => { onView(TourV2ContentSections.ITINERARY) }, [onView])

  const showModal = useContext(ModalContext)
  const requestPrivateTour = useCallback(() => {
    onRequestPrivateTour() // Track click on "Request private tour" button
    showModal(<PrivateRequestFormModal variations={offer.variations} tourId={offer.id} />)
  }, [showModal, offer, onRequestPrivateTour])

  const locationsVisited = useMemo(() => {
    return unique(variation.itinerary
      .flatMap(itinerary => itinerary.locationsVisitedDetails)
      .map(location => location.name))
  }, [variation.itinerary])

  const departureDateText = useMemo(() => {
    if (offer?.departures) {
    // Filtering for only available departures
      const availableDepartures = Object.values(offer.departures).filter(e => e.status === 'available')
      return (availableDepartures.length === 1 ? 'One departure date only: ' : 'Book up until')
    }
  }, [offer.departures])
  return (
    <PrimaryButtonPaletteProvider palette={buttonPaletteValue}>
      <Group direction="vertical" gap={48}>
        <GridContainer className="OfferIntro">
          <ImmersiveSaveButtonPortal>
            <TourBookmarkButton offer={offer} selectedVariation={variation} />
          </ImmersiveSaveButtonPortal>
          <Group
            className="title-area"
            data-testid="tourV2-offer-overview"
            direction="horizontal"
            horizontalAlign="space-between"
            gap={8}
          >
            <VerticalSpacer gap={8}>
              <BodyText variant="large">
                {hasTheSameStartAndEndLocation &&
                  <span>
                    Starts and ends in <b>{endLocation}</b>
                  </span>}
                {!hasTheSameStartAndEndLocation && <span>
                  Starts in <b>{startLocation}</b>. Ends in <b>{endLocation}</b>
                </span>}
              </BodyText>
              <Heading as="h1" variant="heading2">
                {variation.name}
              </Heading>
            </VerticalSpacer>
            {operatorLogo && <OperatorLogoContainer>
              <OperatorLogoImage
            id={operatorLogo.id}
            dpr={2}
            quality="eco"
            width={150}
            alt={operatorLogo.title}
            aria-hidden="true"
          />
            </OperatorLogoContainer>}
          </Group>

          <DetailItemsContainer className="details-area">
            {operatorName && <BodyText variant="large"><span>Operated by: <b>{operatorName}</b></span></BodyText>}
            <VerticalDivider />
            {lastAvailableBookingDate &&
              <BodyText variant="large">
                {departureDateText} &nbsp;
                <TextLink variant="default" onClick={scrollToDepartures}>
                  {lastAvailableBookingDate}
                </TextLink>
              </BodyText>}
          </DetailItemsContainer>

          {isTourReviewsVisible && (
            <div className="review-area">
              <NumberRating
                variant="small"
                total={offer.reviewsTotal}
                rating={offer.reviewsRating}
                ratingSubtitle={offer.reviewsSource === 'le_tour_operator' ? 'Tour Operator rating' : undefined}
              />
            </div>
          )}

          <div className="description-area">
            <Description
              fontSize="medium"
              fontWeight="normal"
              type="compact"
              content={description}
            />
          </div>

          <Group
            className="top-bar-area"
            direction="horizontal"
            horizontalAlign="space-between"
            verticalAlign="center"
            gap={8}
          >
            <Group direction="horizontal" gap={4} wrap="wrap">
              {showSecureWithDepositTag && <DepositsAvailableTag
                offerType={offer.type}
                depositAmountPercentage={offer.depositAmount}
                depositType={offer.depositType}
              />}
              <TourV2UrgencyTags tags={offer.urgencyTags} />
            </Group>

            <Group direction="horizontal" verticalAlign="center" gap={8}>
              <ShareModal shareConfig={shareConfig}>
                <ShareWithFriendsButton size="small" />
              </ShareModal>
              <TripGuard>
                <TourBookmarkButton offer={offer} selectedVariation={variation} />
              </TripGuard>
            </Group>
          </Group>

          <div className="overview-area">
            <OverviewPane>
              <Heading variant="heading5">
                Overview
              </Heading>
              <OverviewPaneDetailItemsContainer>
                <BodyTextBlock variant="medium" colour="neutral-two" startIcon={<LineClockIcon aria-hidden="true" />}>
                  {durationInDaysCount}
                </BodyTextBlock>
                {placesCount && <BodyTextBlock variant="medium" colour="neutral-two" startIcon={<LineMapIcon aria-hidden="true" />}>
                  {placesCount}
                </BodyTextBlock>}
                {countriesCount && <BodyTextBlock variant="medium" colour="neutral-two" startIcon={<LineGlobeIcon aria-hidden="true" />}>
                  {countriesCount}
                </BodyTextBlock>}
                {isVariationLeading && <BodyTextBlock variant="medium" colour="neutral-two" startIcon={<LineTagAltIcon aria-hidden="true" />}>
                  From <strong><FormatCurrency value={pricePerDay} format="roundedDollar" /></strong>/day
                </BodyTextBlock>}
                {maxPax && <BodyTextBlock variant="medium" colour="neutral-two" startIcon={<LineUsersAltIcon aria-hidden="true" />}>
                  {maxPax} people max.
                </BodyTextBlock>}
                {minAge && <BodyTextBlock variant="medium" colour="neutral-two" startIcon={<LineUserSquareIcon aria-hidden="true" />}>
                  {!maxAge ? `${minAge}+ yrs` : `${minAge} - ${maxAge} yrs`}
                </BodyTextBlock>}
                {activityLevel && <BodyTextBlock variant="medium" colour="neutral-two" startIcon={<LineChartLineIcon aria-hidden="true" />} format="titlecase" >
                  {activityLevel}
                </BodyTextBlock>}
                {isPrivateRequestEnabled && <BodyTextBlock variant="medium" colour="neutral-two" startIcon={<LineUserIcon aria-hidden="true" />}>
                  Private tours available
                </BodyTextBlock>}
                <ContainerExtend>
                  <TourV2ItineraryLocationList locationsVisited={locationsVisited} />
                </ContainerExtend>
              </OverviewPaneDetailItemsContainer>
              <Hr colour="light-grey" />
              <Group direction="vertical">
                {!!purchasableOptionDetails && <>
                  {purchasableOptionDetails.pricePerPerson && <>
                    <LabelsGroup direction="horizontal" wrap="wrap" gap={4}>
                      {purchasableOptionDetails.hasDiscount && <UrgencyLabel variant="critical2">Sale</UrgencyLabel>}
                      {showSecureWithDepositTag && <SecureWithDepositTagAndTooltip offerType={offer.type} />}
                    </LabelsGroup>
                    <div>
                      <BodyText variant="medium" colour="neutral-two" as="span">From</BodyText>&nbsp;
                      <Heading variant="heading3" as="span">
                        <FormatCurrency value={purchasableOptionDetails.pricePerPerson} format="roundedDollar" />
                      </Heading>
                      <BodyText variant="medium" as="span">&nbsp;/person</BodyText>
                    </div>
                  </>}
                  {purchasableOptionDetails.hasDiscount && <Group direction="horizontal" wrap="wrap" gap={4}>
                    <Caption variant="large" colour="neutral-three" strikeThrough>
                      Was: <FormatCurrency value={purchasableOptionDetails.fullPricePerPerson} format="roundedDollar" />
                    </Caption>
                  </Group>}
                  {!!purchasableOptionDetails.roomTypeName && <BodyText variant="medium" colour="neutral-two" as="div">{purchasableOptionDetails.roomTypeName}</BodyText>}
                  {!!purchasableOptionDetails.valuedAt && <ValuedAtCaption variant="medium" weight="bold">
                    <span>Valued up to <FormatCurrency value={purchasableOptionDetails.valuedAt.price} /></span>
                    <Pill kind="primary" variant="success">Save up to {purchasableOptionDetails.valuedAt.savingPercentage}%</Pill>
                  </ValuedAtCaption>}
                </>}
                {!isOfferPurchasable && <Heading variant="heading5" align="center">
                  This offer has sold out
                </Heading>}
              </Group>
              <Group direction="vertical" gap={8}>
                {isOfferPurchasable && <TextButton kind="primary" fit="full-width" onClick={scrollToDepartures}>
                  Check Availability
                </TextButton>}
                {!isOfferPurchasable && <TextButton kind="secondary" to="/tours">
                  Browse all tour offers
                </TextButton>}
                <TextButton kind="secondary" fit="full-width" onClick={scrollToItinerary}>
                  View itinerary & map
                </TextButton>
              </Group>
            </OverviewPane>
          </div>
        </GridContainer>
        {isUltraLuxTour(offer) && <UltraLuxUSPs data-testid="tourV2-ultra-lux-usps" onRequestPrivateTour={requestPrivateTour} isPrivateRequest={variation.isPrivateRequest}/>}
        {!isUltraLuxTour(offer) && <TourV2USP onRequestPrivateTour={requestPrivateTour} isPrivateRequest={isPrivateRequestEnabled} />}
      </Group>
    </PrimaryButtonPaletteProvider>
  )
}

const mapOfferPageStateToProps = (offerPageState: OfferPageState, appState: App.State): MappedProps => {
  const { latestDeparture } = getSelectedVariationDepartureBoundaries(offerPageState)
  return {
    latestDeparture,
    offer: offerPageState.tourV2Offer,
    variation: offerPageState.selectedTourV2OfferVariation,
    isOfferPurchasable: getIsTourOfferPurchasable(offerPageState),
    toursPricePerDayABTestVariation: offerPageState.toursPricePerDayABTestVariation,
    isPrivateRequestEnabled: getIsPrivateRequestEnabled(offerPageState, appState),
  }
}

export default withOfferPageAndReduxState(mapOfferPageStateToProps)(TourV2Overview)
